import { Route, UrlSegment, UrlSegmentGroup } from '@angular/router';

/**
 * @whatItDoes Represents the constraint hash for params on a particular route.
 * See {@link @angular/Routes} for more details.
 */
export type ConstraintMatchTestParams = { [paramName: string]: RegExp; };

/**
 * @whatItDoes Represents the constraint callback signature on a particular route segment.
 * See {@link @angular/Routes} for more details.
 */
export type ConstraintMatchTestCallback = (pathSegment: string) => boolean;

/**
 * @whatItDoes Allows to constrain route params
 *
 * @description
 *
 * Provides a custom URL matcher based on the default Angular 2 matcher.
 * The matcher does the same matching as defaultUrlMatcher
 * See {@link @angular/Routes} for defaultUrlMatcher
 *
 * For instance, the following matcher matches regular expressions.
 *
 * ```
 * const routes = [{
 *     matcher: createConstraintMatcher('path/:productId', {productId: /^\d+$/}),
 *     component: HtmlCmp
 * }];
 * ```
 */
// source https://gist.github.com/scottasmith/4fd2669489d2709b48ceefc24b0e3851
export function createConstraintMatcher(path: string, testParams: ConstraintMatchTestParams) {
    return (segments: UrlSegment[], segmentGroup: UrlSegmentGroup, route: Route) => {
        const parts = path.split('/');
        const posParams: { [key: string]: UrlSegment; } = {};
        const consumed: UrlSegment[] = [];

        let currentIndex = 0;

        for (const p of parts) {
            if (currentIndex >= segments.length) {
                return null;
            }

            const current = segments[currentIndex];

            const isPosParam = p.startsWith(':');

            if (!isPosParam && p !== current.path) {
                return null;
            }

            const paramName = p.substring(1);

            if (isPosParam && testParams[paramName]) {
                const testParam: RegExp = testParams[paramName];

                if (!testParam.test(current.path)) {
                    return null;
                }

                // If we get this far then we are success
                posParams[paramName] = current;
            } else if (isPosParam) {
                posParams[paramName] = current;
            }

            consumed.push(current);
            currentIndex++;
        }

        if (route.pathMatch === 'full' && (segmentGroup.hasChildren() || currentIndex < segments.length)) {
            return null;
        } else {
            return { consumed, posParams };
        }
    };
};
